import React, { createContext, useContext, ReactNode } from 'react';
import { Notification } from '../types/notification';
import { Goal } from '../types/goals';
import { Achievement } from '../types/goals';
import { useNotifications } from '../hooks/useNotifications';
import useGoalReminders from '../hooks/useGoalReminders';
import logger from '../utils/logger';
// Import dynamically to avoid circular dependencies

// Define the context type
interface NotificationContextType {
  // Standard notifications
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  hasMore: boolean;
  loadNotifications: (limit?: number) => void;
  loadMoreNotifications: (limit?: number) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  deleteNotification: (id: string) => void;
  
  // Goal reminders
  goalReminders: { goal: Goal; timestamp: number }[];
  refreshReminders: () => void;
  scheduleReminder: (goal: Goal, reminderTime: number) => Promise<{ success: boolean }>;
  cancelReminders: (goalId: string) => Promise<{ success: boolean }>;
  dismissReminder: (goalId: string, timestamp: number) => Promise<{ success: boolean }>;
  
  // Achievement notifications
  showAchievement: (achievement: Achievement) => void;
}

// Create the context
const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

// Provider component
interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  // Standard notifications
  const {
    notifications,
    unreadCount,
    isLoading,
    hasMore,
    loadNotifications,
    loadMoreNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } = useNotifications();
  
  // Goal reminders
  const {
    reminders: goalReminders,
    refreshReminders,
    scheduleReminder,
    cancelReminders,
    dismissReminder,
  } = useGoalReminders();
  
  // Achievement notifications stub
  // We need to provide a stub implementation since the real implementation
  // would cause circular dependencies
  const showAchievement = (achievement: Achievement) => {
    logger.info('Achievement notification would show:', achievement);
    // This is just a stub - the actual rendering happens through AchievementNotificationProvider
  };
  
  // Combine everything into the context value
  const contextValue: NotificationContextType = {
    // Standard notifications
    notifications,
    unreadCount,
    isLoading,
    hasMore,
    loadNotifications,
    loadMoreNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    
    // Goal reminders
    goalReminders,
    refreshReminders,
    scheduleReminder,
    cancelReminders,
    dismissReminder,
    
    // Achievement notifications
    showAchievement,
  };
  
  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};

// Custom hook to use the notification context
export const useNotificationContext = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  
  if (!context) {
    throw new Error('useNotificationContext must be used within a NotificationProvider');
  }
  
  return context;
};